import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { useParametroFederal, useSalvarParametroFederal } from '@/services/Configuracoes';
import type { ParametroFederalApi } from '@/services/Configuracoes';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import type { ParametrosFederal } from '@/lib/types/configuracoes';

const indicadorOptions = [
  { value: '0', label: '0 - Industrial ou Equiparado a Industria' },
  { value: '1', label: '1 - Prestador de Serviços' },
  { value: '2', label: '2 - Atividade de Comercio' },
  { value: '3', label: '3 - Atividade Financeira' },
  { value: '4', label: '4 - Atividade Imobiliária' },
  { value: '9', label: '9 - Outros' },
  { value: 'SN_D', label: 'Simples Nacional - IBS/CBS por dentro da data da opção' },
  { value: 'SN_F', label: 'Simples Nacional - IBS/CBS por fora da data da opção' },
];

const regimeOptions = [
  { value: '1', label: '1 - Simples Nacional' },
  { value: '2', label: '2 - Simples Nacional (excesso sublimite rec. bruta)' },
  { value: 'P', label: 'P - Cumulativo' },
  { value: 'R', label: 'R - Não Cumulativo' },
];

const regimeApuracaoOptions = [
  { value: '1', label: '1 - Escrituração com Incidência Exclusivamente Regime Não Cumulativo' },
  { value: '2', label: '2 - Escrituração com Incidência Exclusivamente Regime Cumulativo' },
  { value: '3', label: '3 - Escrituração com Incidência de Ambos os Regimes' },
];

const metodoCreditosOptions = [
  { value: '1', label: '1 - Método de Apropriação Direta' },
  { value: '2', label: '2 - Método de Rateio Proporcional' },
];

const tipoContribuicaoOptions = [
  { value: '1', label: '1 - Exclusivamente a Alíquota Básica' },
  { value: '2', label: '2 - Exclusivamente a Alíquota por Unidade de Produto' },
  { value: '3', label: '3 - Ambas as Formas (Alíquota Básica e por Unidade)' },
  { value: '9', label: '9 - Outras' },
];

const codContribuicaoOptions = [
  { value: '01', label: '01 - Contribuição não-cumulativa apurada a alíquota básica' },
  { value: '02', label: '02 - Contribuição não-cumulativa apurada a alíquota diferenciada' },
  { value: '03', label: '03 - Contribuição não-cumulativa apurada por unidade de produto' },
  { value: '04', label: '04 - Contribuição tributada no mercado interno - regime cumulativo' },
  { value: '05', label: '05 - Contribuição tributada a alíquota zero' },
  { value: '06', label: '06 - Contribuição não tributada (não incidência)' },
  { value: '07', label: '07 - Contribuição com suspensão de exigibilidade' },
  { value: '09', label: '09 - Outras contribuições' },
];

const checkboxLabels = [
  {
    field: 'assumirCst70',
    label:
      'Assumir CST 70 para PIS/COFINS para notas de Produtor Rural (Tributadas no Cad. de Produtos)',
  },
  { field: 'consistirCfopCst', label: 'Consistir combinação CFOPxCST ICMS na Saída' },
  {
    field: 'consistirValorPisCofins',
    label: 'Consistir Valor do PIS/COFINS na Saída com Cadastro',
  },
  {
    field: 'incluirIpiBase',
    label: 'Incluir IPI na Base de Cálculo do PIS/COFINS para fins de crédito IN 404/04.',
  },
  { field: 'processarBlocoP', label: 'Processar "Bloco P" (Contr. Prev.)' },
  {
    field: 'naoInformarContasContabeis',
    label: 'Não Informar Contas Contábeis nos Registros (Ignorar COD_CTA)',
  },
  {
    field: 'considerarMatriz',
    label: 'Considerar este CNPJ como sendo Matriz no Registro 0000 do PVA Federal',
  },
  {
    field: 'gerarCadastroAtividade',
    label: 'Gerar Cadastro Usando a Atividade como Default para o TipoItem',
  },
] as const;

type BooleanField = (typeof checkboxLabels)[number]['field'];

function isSimples(regime: string) {
  return regime === '1' || regime === '2';
}

const defaultParams: ParametrosFederal = {
  indicadorAtividade: '2',
  regimeTributario: 'R',
  regimeApuracaoCS: '1',
  metodoCreditosComuns: '1',
  tipoContribuicao: '1',
  codContribuicaoDefault: '01',
  naturezaRendimento: '',
  presuncaoIRPJ: { comercio: 8, servico: 16, csll: 32 },
  blocoM: {
    pisCumulativo: '',
    cofinsCumulativo: '',
    pisNaoCumulativo: '',
    cofinsNaoCumulativo: '',
  },
  contaContabilF100: '',
  assumirCst70: false,
  consistirCfopCst: false,
  consistirValorPisCofins: false,
  incluirIpiBase: false,
  processarBlocoP: false,
  codigoAtivBlocoP: '',
  naoInformarContasContabeis: false,
  considerarMatriz: false,
  gerarCadastroAtividade: false,
  simplesNacional: { dataOpcao: '', rbt12Atual: '', faixaAtual: '' },
  ibsCbs: { aliquotaIBSUF: '0,10%', aliquotaIBSMunicipal: '0,05%', aliquotaCBS: '0,90%' },
};

function apiToParams(api: ParametroFederalApi): ParametrosFederal {
  return {
    indicadorAtividade: (api.IndicadorAtividade as ParametrosFederal['indicadorAtividade']) ?? '2',
    regimeTributario: (api.RegimeTributario as ParametrosFederal['regimeTributario']) ?? 'R',
    regimeApuracaoCS: api.RegimeApuracaoCS ?? '1',
    metodoCreditosComuns: api.MetodoCreditosComuns ?? '1',
    tipoContribuicao: api.TipoContribuicao ?? '1',
    codContribuicaoDefault: api.CodContribuicaoDefault ?? '01',
    naturezaRendimento: api.NaturezaRendimento ?? '',
    presuncaoIRPJ: {
      comercio: api.PresuncaoComercio ?? 8,
      servico: api.PresuncaoServico ?? 16,
      csll: api.PresuncaoCsll ?? 32,
    },
    blocoM: {
      pisCumulativo: api.BlocoMPisCumulativo ?? '',
      cofinsCumulativo: api.BlocoMCofinsCumulativo ?? '',
      pisNaoCumulativo: api.BlocoMPisNaoCumulativo ?? '',
      cofinsNaoCumulativo: api.BlocoMCofinsNaoCumulativo ?? '',
    },
    contaContabilF100: api.ContaContabilF100 ?? '',
    assumirCst70: api.AssumirCst70 === 'S',
    consistirCfopCst: api.ConsistirCfopCst === 'S',
    consistirValorPisCofins: api.ConsistirValorPisCofins === 'S',
    incluirIpiBase: api.IncluirIpiBase === 'S',
    processarBlocoP: api.ProcessarBlocoP === 'S',
    codigoAtivBlocoP: api.CodigoAtivBlocoP ?? '',
    naoInformarContasContabeis: api.NaoInformarContasContabeis === 'S',
    considerarMatriz: api.ConsiderarMatriz === 'S',
    gerarCadastroAtividade: api.GerarCadastroAtividade === 'S',
    simplesNacional: { dataOpcao: '', rbt12Atual: '', faixaAtual: '' },
    ibsCbs: { aliquotaIBSUF: '0,10%', aliquotaIBSMunicipal: '0,05%', aliquotaCBS: '0,90%' },
  };
}

function paramsToApi(params: ParametrosFederal): Partial<ParametroFederalApi> {
  return {
    IndicadorAtividade: params.indicadorAtividade,
    RegimeTributario: params.regimeTributario,
    RegimeApuracaoCS: params.regimeApuracaoCS,
    MetodoCreditosComuns: params.metodoCreditosComuns,
    TipoContribuicao: params.tipoContribuicao,
    CodContribuicaoDefault: params.codContribuicaoDefault,
    NaturezaRendimento: params.naturezaRendimento,
    PresuncaoComercio: params.presuncaoIRPJ.comercio,
    PresuncaoServico: params.presuncaoIRPJ.servico,
    PresuncaoCsll: params.presuncaoIRPJ.csll,
    BlocoMPisCumulativo: params.blocoM.pisCumulativo,
    BlocoMCofinsCumulativo: params.blocoM.cofinsCumulativo,
    BlocoMPisNaoCumulativo: params.blocoM.pisNaoCumulativo,
    BlocoMCofinsNaoCumulativo: params.blocoM.cofinsNaoCumulativo,
    ContaContabilF100: params.contaContabilF100,
    AssumirCst70: params.assumirCst70 ? 'S' : 'N',
    ConsistirCfopCst: params.consistirCfopCst ? 'S' : 'N',
    ConsistirValorPisCofins: params.consistirValorPisCofins ? 'S' : 'N',
    IncluirIpiBase: params.incluirIpiBase ? 'S' : 'N',
    ProcessarBlocoP: params.processarBlocoP ? 'S' : 'N',
    CodigoAtivBlocoP: params.codigoAtivBlocoP,
    NaoInformarContasContabeis: params.naoInformarContasContabeis ? 'S' : 'N',
    ConsiderarMatriz: params.considerarMatriz ? 'S' : 'N',
    GerarCadastroAtividade: params.gerarCadastroAtividade ? 'S' : 'N',
  };
}

export function FederalTab() {
  const { data: response, isLoading } = useParametroFederal();
  const salvar = useSalvarParametroFederal();
  const [params, setParams] = useState<ParametrosFederal>(defaultParams);

  useEffect(() => {
    if (response?.data) {
      setParams(apiToParams(response.data));
    }
  }, [response]);

  const set = <K extends keyof ParametrosFederal>(field: K, value: ParametrosFederal[K]) => {
    setParams(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Configurações Gerais */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Configurações Gerais</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>Indicador de Ativ. (FEDERAL):</Label>
            <Select
              value={params.indicadorAtividade}
              onValueChange={v =>
                set('indicadorAtividade', v as ParametrosFederal['indicadorAtividade'])
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {indicadorOptions.map(o => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Regime Tributário (FEDERAL):</Label>
            <Select
              value={params.regimeTributario}
              onValueChange={v =>
                set('regimeTributario', v as ParametrosFederal['regimeTributario'])
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {regimeOptions.map(o => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Regime de Apuração da CS e Apropriação de Créditos (0110):</Label>
            <Select value={params.regimeApuracaoCS} onValueChange={v => set('regimeApuracaoCS', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {regimeApuracaoOptions.map(o => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Método de Apropriação de Créditos Comuns (0110):</Label>
            <Select
              value={params.metodoCreditosComuns}
              onValueChange={v => set('metodoCreditosComuns', v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {metodoCreditosOptions.map(o => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Código Indicador do Tipo de Contribuição Apurada (0110):</Label>
            <Select value={params.tipoContribuicao} onValueChange={v => set('tipoContribuicao', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {tipoContribuicaoOptions.map(o => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Código de Contribuição (PIS/COFINS) Default para Saídas/Débitos (4.3.5):</Label>
            <Select
              value={params.codContribuicaoDefault}
              onValueChange={v => set('codContribuicaoDefault', v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {codContribuicaoOptions.map(o => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>
              Natureza de Rendimento Padrão para Integração de NFS Prestado com o Registro R4080
              (REINF):
            </Label>
            <Select
              value={params.naturezaRendimento || 'none'}
              onValueChange={v => set('naturezaRendimento', v === 'none' ? '' : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhum</SelectItem>
                <SelectItem value="13858">13858 - Outros rendimentos</SelectItem>
                <SelectItem value="25045">25045 - Rendimentos do trabalho</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Presunção do IRPJ */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Presunção do IRPJ</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>Comércio (%):</Label>
              <Input
                type="number"
                value={params.presuncaoIRPJ.comercio}
                onChange={e =>
                  set('presuncaoIRPJ', {
                    ...params.presuncaoIRPJ,
                    comercio: Number(e.target.value),
                  })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label>Serviço (%):</Label>
              <Input
                type="number"
                value={params.presuncaoIRPJ.servico}
                onChange={e =>
                  set('presuncaoIRPJ', { ...params.presuncaoIRPJ, servico: Number(e.target.value) })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label>CSLL (%):</Label>
              <Input
                type="number"
                value={params.presuncaoIRPJ.csll}
                onChange={e =>
                  set('presuncaoIRPJ', { ...params.presuncaoIRPJ, csll: Number(e.target.value) })
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bloco M */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Bloco M — Códigos de Contribuição da Receita</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>PIS Cumulativo:</Label>
              <Input
                value={params.blocoM.pisCumulativo}
                onChange={e => set('blocoM', { ...params.blocoM, pisCumulativo: e.target.value })}
                placeholder="Ex: 691101"
              />
            </div>
            <div className="space-y-1.5">
              <Label>COFINS Cumulativo:</Label>
              <Input
                value={params.blocoM.cofinsCumulativo}
                onChange={e =>
                  set('blocoM', { ...params.blocoM, cofinsCumulativo: e.target.value })
                }
                placeholder="Ex: 585501"
              />
            </div>
            <div className="space-y-1.5">
              <Label>PIS Não Cumulativo:</Label>
              <Input
                value={params.blocoM.pisNaoCumulativo}
                onChange={e =>
                  set('blocoM', { ...params.blocoM, pisNaoCumulativo: e.target.value })
                }
                placeholder="Ex: 691201"
              />
            </div>
            <div className="space-y-1.5">
              <Label>COFINS Não Cumulativo:</Label>
              <Input
                value={params.blocoM.cofinsNaoCumulativo}
                onChange={e =>
                  set('blocoM', { ...params.blocoM, cofinsNaoCumulativo: e.target.value })
                }
                placeholder="Ex: 585601"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Conta Contábil F100 */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Conta Contábil p/ Ajuste Automático</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1.5">
            <Label>Registro F100:</Label>
            <Input
              value={params.contaContabilF100}
              onChange={e => set('contaContabilF100', e.target.value)}
              placeholder="Ex: 411001"
              className="max-w-xs"
            />
          </div>
        </CardContent>
      </Card>

      {/* Opções / Checkboxes */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Opções</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {checkboxLabels.map(({ field, label }) => (
            <div key={field} className="flex items-start gap-2.5">
              <Checkbox
                id={field}
                checked={params[field as BooleanField] as boolean}
                onCheckedChange={checked =>
                  set(field as keyof ParametrosFederal, Boolean(checked) as never)
                }
                className="mt-0.5"
              />
              <Label htmlFor={field} className="text-sm font-normal leading-snug cursor-pointer">
                {label}
                {field === 'processarBlocoP' && (
                  <Input
                    value={params.codigoAtivBlocoP}
                    onChange={e => set('codigoAtivBlocoP', e.target.value)}
                    placeholder="Código Ativ."
                    className="inline-flex h-7 w-32 ml-2 text-xs"
                    onClick={e => e.stopPropagation()}
                  />
                )}
              </Label>
            </div>
          ))}

          <Separator className="my-1" />

          <Button variant="outline" size="sm">
            Exclusão do ICMS da Base de Cálculo do PIS/COFINS
          </Button>
        </CardContent>
      </Card>

      {/* Condicional: Simples Nacional */}
      {isSimples(params.regimeTributario) && (
        <Card className="border-green-200 bg-green-50/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-green-800">Informações Simples Nacional</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-green-700">Data Opção:</Label>
                <p className="text-sm font-medium bg-white/70 rounded-md px-3 py-2 border border-green-200">
                  {params.simplesNacional.dataOpcao}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-green-700">RBT12 Atual:</Label>
                <p className="text-sm font-medium bg-white/70 rounded-md px-3 py-2 border border-green-200">
                  {params.simplesNacional.rbt12Atual}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-green-700">Faixa Atual:</Label>
                <p className="text-sm font-medium bg-white/70 rounded-md px-3 py-2 border border-green-200">
                  {params.simplesNacional.faixaAtual}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Condicional: IBS/CBS */}
      {!isSimples(params.regimeTributario) && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-blue-800">Alíquotas IBS/CBS (2026)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-blue-700">Alíquota IBS UF (2026):</Label>
                <p className="text-sm font-medium bg-white/70 rounded-md px-3 py-2 border border-blue-200">
                  {params.ibsCbs.aliquotaIBSUF}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-blue-700">Alíquota IBS Municipal (2026):</Label>
                <p className="text-sm font-medium bg-white/70 rounded-md px-3 py-2 border border-blue-200">
                  {params.ibsCbs.aliquotaIBSMunicipal}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-blue-700">Alíquota CBS (2026):</Label>
                <p className="text-sm font-medium bg-white/70 rounded-md px-3 py-2 border border-blue-200">
                  {params.ibsCbs.aliquotaCBS}
                </p>
              </div>
            </div>
            <p className="text-xs text-blue-600 italic">
              * Alíquotas conforme período de transição da Lei Complementar 214/2025
            </p>
          </CardContent>
        </Card>
      )}

      {/* Botão Salvar */}
      <div className="flex justify-end pt-2">
        <Button
          onClick={() => salvar.mutate(paramsToApi(params))}
          disabled={salvar.isPending}
          className="min-w-32"
        >
          {salvar.isPending ? 'Salvando...' : 'Salvar Parâmetros'}
        </Button>
      </div>
    </div>
  );
}
